import React, { useState, useCallback } from "react";
import { Col, Input } from "reactstrap";
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { ReactComponent as FileIcon } from '../../../../../assets/images/file.svg';
import { ReactComponent as TrashIcon } from '../../../../../assets/images/delete-button.svg';
import { ReactComponent as DownloadIcon } from '../../../../../assets/images/download-black.svg';
import searchIcon from '../../../../../assets/images/search-icon.png';
import dragIIcon from '../../../../../assets/images/drag-icon.svg';
import { ContentService } from '../../../../../globals/services/content.service';
import styles from './index.module.scss';
import DragTitle from "../../../components/DragTitle";

interface ContentItem {
    _id: string;
    title: string;
    description?: string;
    order?: number;
    status?: string;
    type?: string;
}

interface ContentItemsSectionProps {
    contentItems?: ContentItem[];
    contentItemList?: ContentItem[];
    onContentItemsChange: (updatedContentItems: ContentItem[]) => void;
    onContentItemPreview?: (content: any) => void;
}

const ContentItemsSection = ({
    contentItems = [],
    contentItemList = [],
    onContentItemsChange,
    onContentItemPreview
}: ContentItemsSectionProps) => {
    const contentService = ContentService();
    
    const [filteredContentItemList, setFilteredContentItemList] = useState<ContentItem[]>([]);
    const [searchPerformed, setSearchPerformed] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [addingItems, setAddingItems] = useState<Set<string>>(new Set());

    const performClientSearch = useCallback((searchTerm) => {
        if (!searchTerm || searchTerm.length < 3) {
            setFilteredContentItemList([]);
            setSearchPerformed(false);
            setSearchLoading(false);
            return;
        }

        setSearchLoading(true);
        setTimeout(() => {
            try {
                const filtered = contentItemList.filter((item) => {
                    return item.title && item.title.toLowerCase().includes(searchTerm.toLowerCase());
                });
                setFilteredContentItemList(filtered);
                setSearchPerformed(true);
            } catch (error) {
                console.error('Search error:', error);
                setFilteredContentItemList([]);
                setSearchPerformed(true);
            } finally {
                setSearchLoading(false);
            }
        }, 0);
    }, [contentItemList]);

    const handleSearch = useCallback((searchTerm) => {
        if (searchTerm.length >= 3) {
            performClientSearch(searchTerm);
            setSearchLoading(true);
        } else if (searchTerm.length === 0) {
            setFilteredContentItemList([]);
            setSearchPerformed(false);
            setSearchLoading(false);
        }
    }, [performClientSearch]);

    const calculateNextContentItemOrder = () => {
        let arrOfOrderItems = contentItems
            .map((item) => item.order)
            .filter((order): order is number => order !== undefined);
        let order;
        if (arrOfOrderItems.length === 0) {
            order = 1;
        } else {
            order = Math.max(...arrOfOrderItems) + 1;
        }
        return order;
    };

    const handleContentItemToFolder = (e: React.MouseEvent, id: string) => {
        e.preventDefault();
        
        setAddingItems(prev => new Set(prev).add(id));
        
        // Simulate a brief delay for better UX
        setTimeout(() => {
            let item = contentItemList.find((item) => item._id === id);
            if (!item) return;
            
            let itemToFolder = {
                _id: item._id,
                title: item.title,
                description: item.description,
                order: calculateNextContentItemOrder(),
                status: "active",
                type: item.type,
            }
            const updatedContentItems = [...contentItems, itemToFolder];
            onContentItemsChange(updatedContentItems);
            
            // Remove from adding state
            setAddingItems(prev => {
                const newSet = new Set(prev);
                newSet.delete(id);
                return newSet;
            });
        }, 500);
    };

    const handleRemoveContentItemFromFolder = (e: React.MouseEvent, id: string) => {
        e.preventDefault();
        
        setAddingItems(prev => new Set(prev).add(id));
        
        setTimeout(() => {
            let index = contentItems.findIndex(item => item._id === id);
            if (index > -1) {
                const updatedContentItems = contentItems.filter((_, i) => i !== index);
                onContentItemsChange(updatedContentItems);
            }
            
            setAddingItems(prev => {
                const newSet = new Set(prev);
                newSet.delete(id);
                return newSet;
            });
        }, 500);
    };

    const handleRemoveContentItem = (e: React.MouseEvent, item: ContentItem) => {
        e.preventDefault();
        const updatedContentItems = contentItems.filter(contentItem => contentItem._id !== item._id);
        onContentItemsChange(updatedContentItems);
    };

    const openContentItemPreview = (e: React.MouseEvent, _id: string) => {
        e.stopPropagation();
        contentService.getContentItemById(_id).then((content) => {
            if (content[0].type === 'file') {
                window.open(content[0].file, '_blank');
            } else {
                onContentItemPreview && onContentItemPreview(content[0]);
            }
        });
    };

    const onDragEnd = (result: DropResult) => {
        const { destination, source, draggableId } = result;

        // If dropped outside the list
        if (!destination) {
            return;
        }

        // If dropped in the same position
        if (destination.droppableId === source.droppableId && destination.index === source.index) {
            return;
        }

        // Create new array with reordered items
        const newContentItems = Array.from(contentItems);
        const [reorderedItem] = newContentItems.splice(source.index, 1);
        newContentItems.splice(destination.index, 0, reorderedItem);

        // Update order property for all items
        const updatedContentItems = newContentItems.map((item, index) => ({
            ...item,
            order: index + 1
        }));

        onContentItemsChange(updatedContentItems);
    };

    return (
        <div className={styles.section}>
            <h2>{`Content Items ${contentItems?.length > 0 ? `(${contentItems.length})` : ''}`}</h2>
            <Col className="col-12 col-md-8">
                <div className={styles.searchContainer}>
                    <img src={searchIcon} alt="search" className={styles.searchIcon} />
                    <Input
                        name="searchContentItems"
                        type="text"
                        onChange={(e) => handleSearch(e.target.value)}
                        placeholder={contentItemList?.length === 0 ? "Loading content items..." : "Add documents to this folder"}
                        className={styles.searchInput}
                        disabled={contentItemList?.length === 0}
                    />
                    {contentItemList.length === 0 && (
                        <div className={styles.inputSpinner}></div>
                    )}
                </div>
                {searchLoading && (
                    <div className={styles.searchStateContainer}>
                        <div className={styles.searchLoadingState}>
                            <div className={styles.spinner}></div>
                            <span className={styles.loadingText}>Searching content items...</span>
                        </div>
                    </div>
                )}
                {!searchLoading && searchPerformed && filteredContentItemList.length === 0 && (
                    <div className={styles.searchStateContainer}>
                        <div className={styles.searchNoResultsState}>
                            <div className={styles.noResultsIcon}>🔍</div>
                            <h4 className={styles.noResultsTitle}>
                                {contentItemList?.length === 0
                                    ? 'No Content Items Available'
                                    : 'No Results Found'}
                            </h4>
                            <p className={styles.noResultsDescription}>
                                {contentItemList?.length === 0
                                    ? 'Content items haven\'t been loaded yet, please wait for the system to fetch available content.'
                                    : 'No content items match your search criteria, try adjusting your search terms.'}
                            </p>
                        </div>
                    </div>
                )}
                {!searchLoading && filteredContentItemList && filteredContentItemList.length > 0 && (
                    <div className={styles.searchResultsContainer}>
                        <div className={styles.resultsHeader}>
                            <span className={styles.resultsCount}>{filteredContentItemList.length} result{filteredContentItemList.length !== 1 ? 's' : ''} found</span>
                        </div>
                        <div className={styles.resultsList}>
                            {filteredContentItemList.map((item, index) => (
                                <div className={styles.contentItemCard} key={`${item._id}-${index}`}>
                                    <div className={styles.contentItemInfo}>
                                        <FileIcon className={styles.contentItemIcon} />
                                        <div className={styles.contentItemTitle}>{item.title}</div>
                                    </div>
                                    <button
                                        className={styles.addButton}
                                        data-action={contentItems && contentItems.some(contentItem => contentItem._id === item._id) ? 'remove' : 'add'}
                                        onClick={(e) => {
                                            const isInFolder = contentItems && contentItems.some(contentItem => contentItem._id === item._id);
                                            if (isInFolder) {
                                                handleRemoveContentItemFromFolder(e, item._id);
                                            } else {
                                                handleContentItemToFolder(e, item._id);
                                            }
                                        }}
                                        disabled={addingItems.has(item._id)}
                                    >
                                        {addingItems.has(item._id) 
                                            ? (contentItems && contentItems.some(contentItem => contentItem._id === item._id) ? 'Removing...' : 'Adding...')
                                            : (contentItems && contentItems.some(contentItem => contentItem._id === item._id) ? 'Remove' : 'Add to folder')
                                        }
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {contentItems && contentItems.length > 0 && (
                    <Col className={styles.contentItemContainer}>
                        <DragDropContext onDragEnd={onDragEnd}>
                            <DragTitle text="Drag to reorder your documents" />

                            <div>
                                <Droppable droppableId="content-items">
                                    {(provided, snapshot) => (
                                        <div
                                            {...provided.droppableProps}
                                            ref={provided.innerRef}
                                            className={`${styles.droppableArea} ${snapshot.isDraggingOver ? styles.draggingOver : ''}`}
                                        >
                                            {contentItems && contentItems.length > 0 && contentItems
                                                .sort((a, b) => (a.order || 0) - (b.order || 0))
                                                .map((item, index) => (
                                                <Draggable key={item._id} draggableId={item._id} index={index}>
                                                    {(provided, snapshot) => (
                                                        <div
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            className={styles.draggableItem}
                                                        >
                                                            <div {...provided.dragHandleProps} className={styles.dragHandle}>
                                                                <img src={dragIIcon} alt="Drag Handle" />
                                                            </div>
                                                            <div className={`${styles.itemContent} ${snapshot.isDragging ? styles.dragging : ''}`}>
                                                                <FileIcon className={styles.documentIcon} />
                                                                <div className={styles.contentItemTitle}>{item.title}</div>
                                                                <div className={styles.actionButtons}>
                                                                    <DownloadIcon 
                                                                        className={styles.actionIcon} 
                                                                        onClick={(e) => openContentItemPreview(e, item._id)} 
                                                                        title="Open document"
                                                                    />
                                                                    <div 
                                                                        className={styles.removeButton} 
                                                                        onClick={(e) => handleRemoveContentItem(e, item)}
                                                                        title="Remove from folder"
                                                                    >
                                                                        <TrashIcon className={styles.trashIcon} />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </Draggable>
                                            ))}
                                            {provided.placeholder}
                                        </div>
                                    )}
                                </Droppable>
                            </div>
                        </DragDropContext>
                    </Col>
                )}
            </Col>
        </div>
    );
};

export default ContentItemsSection;
